import { useFormContext } from 'react-hook-form';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { Checkbox, ChipInput, SelectableCampaignsGrid } from 'components/common';
import type { CampaignEntry } from '../api';
import { useCampaignsData } from '../hooks';
import CreateCampaignButton from './CreateCampaignButton';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { toIdSelectionEntries } from '../utils/idSelection';

const CAMPAIGNS_LIMIT = 20;
const EMPTY_SELECTION: string[] = [];

const DataGridCampaign = () => {
  const { watch, setValue, trigger } = useFormContext();
  const selectedCampaigns =
    (watch('selectedCampaigns') as string[] | undefined) ?? EMPTY_SELECTION;
  const { setSelectedCampaigns: setSelectedCampaignsParam, setSelectedAdSets: setSelectedAdSetsParam } =
    useLocationParams();

  const COLUMNS = useMemo<GridColDef<CampaignEntry>[]>(() => [
    { field: 'campaign_id', headerName: 'Campaign ID', width: 160 },
    { field: 'campaign_name', headerName: 'Campaign Name', width: 750 },
  ], []);

  const { gridRows, isLoading, isError, error } = useCampaignsData();

  const handleSelectionMode = useCallback(
    (mergedSelection: string[]) => {
      setValue('selectedCampaigns', mergedSelection, { shouldValidate: true });
      setValue('selectedAdGroups', [], { shouldValidate: true });
      setSelectedAdSetsParam([]);
    },
    [setValue, setSelectedAdSetsParam],
  );

  const getRowId = useCallback((row: CampaignEntry) => row.campaign_id, []);

  useEffect(() => {
    setSelectedCampaignsParam(toIdSelectionEntries(selectedCampaigns));
  }, [selectedCampaigns, setSelectedCampaignsParam]);

  useEffect(() => {
    void trigger('selectedCampaigns');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_campaign_query',
    });
  }, [isError, error]);

  return (
    <SelectableCampaignsGrid
      rows={gridRows}
      columns={COLUMNS}
      getRowId={getRowId}
      selectedIds={selectedCampaigns}
      onSelectionChange={handleSelectionMode}
      limit={CAMPAIGNS_LIMIT}
      limitMessage={`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
      isLoading={isLoading}
    />
  );
};

const ChooseCampaign: FC = memo(() => {
  const { watch, formState: { errors } } = useFormContext();
  const isCampaignFieldError = errors?.selectedCampaigns?.message as string;
  const filterCampaignsByName = watch('filterCampaignsByName') as string[] | undefined;
  const { setFilterCampaignsByName: setFilterCampaignsByNameParam } = useLocationParams();

  useEffect(() => {
    setFilterCampaignsByNameParam(filterCampaignsByName ?? []);
  }, [filterCampaignsByName, setFilterCampaignsByNameParam]);

  return (
    <FormContainer
      title={'Choose Campaigns'}
      error={isCampaignFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'start',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <CreateCampaignButton />
        </Box>
      </Box>
      <DataGridCampaign />
    </FormContainer>
  );
});
ChooseCampaign.displayName = 'ChooseCampaign';

export default ChooseCampaign;